/**
 * The routine's open questions, answered as one batch.
 *
 * At the very top of the detail pane, above *Where things stand*: a question is
 * the routine stopped and waiting, and nothing else on the task moves until it
 * is answered. Every question has to be answered before the batch can go —
 * half a batch would come back to the routine as half an answer.
 */

import { useEffect, useMemo, useState } from 'react';
import { isOpenQuestion, type QuestionAnswer, type Task, type TaskQuestion } from '@shared/domain.ts';
import { useRigg } from '@/data/RiggProvider.tsx';
import { api } from '@/lib/api.ts';
import { cn } from '@/lib/format.ts';
import { Button, Textarea, inputClass, useToast } from './ui.tsx';

/** The "Other" choice — a value no routine-written option can collide with. */
const OTHER = '\u0000other';

interface Draft {
  choice: string | null;
  text: string;
}

/** Open questions on a task, in the order they were asked. */
export function openQuestionsFor(questions: Record<string, TaskQuestion>, taskId: string): TaskQuestion[] {
  return Object.values(questions)
    .filter((q) => q.task_id === taskId && isOpenQuestion(q))
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.position - b.position);
}

function isAnswered(question: TaskQuestion, draft: Draft | undefined): boolean {
  if (!draft) return false;
  if (question.kind === 'text') return draft.text.trim().length > 0;
  if (draft.choice === OTHER) return draft.text.trim().length > 0;
  return Boolean(draft.choice);
}

function toAnswer(question: TaskQuestion, draft: Draft): QuestionAnswer {
  if (question.kind === 'text' || draft.choice === OTHER) {
    return { question_id: question.id, text: draft.text.trim() };
  }
  return { question_id: question.id, choice: draft.choice };
}

export function OpenQuestions({ task }: { task: Task }) {
  const { data } = useRigg();
  const { notify } = useToast();
  const questions = useMemo(() => openQuestionsFor(data.questions, task.id), [data.questions, task.id]);
  const agent = task.agent_id ? data.agents[task.agent_id] : null;

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [sending, setSending] = useState(false);

  // A new batch replaces the old one wholesale, so what was typed against the
  // old questions has nothing left to belong to.
  const batch = questions[0]?.batch_id ?? null;
  useEffect(() => setDrafts({}), [batch, task.id]);

  if (questions.length === 0) return null;

  const set = (id: string, patch: Partial<Draft>) =>
    setDrafts((was) => ({ ...was, [id]: { choice: null, text: '', ...was[id], ...patch } }));

  const remaining = questions.filter((q) => !isAnswered(q, drafts[q.id])).length;

  const send = async () => {
    if (remaining > 0) return;
    setSending(true);
    try {
      await api.answerQuestions(
        task.id,
        questions.map((q) => toAnswer(q, drafts[q.id]!)),
      );
      notify(questions.length === 1 ? 'Answer sent' : 'Answers sent', {
        body: task.pr_url ? 'Posted to the pull request, where the routine picks it up.' : undefined,
      });
    } catch (error) {
      notify('Could not send the answers', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      aria-label="Questions from the routine"
      className="mb-6 rounded-xl border border-amberx/30 bg-amberx/8 px-4 py-3.5"
    >
      <div className="flex items-baseline gap-2">
        <h3 className="m-0 text-[13.5px] font-[650] text-hi">
          {questions.length === 1
            ? `${agent?.name ?? 'The routine'} has a question`
            : `${agent?.name ?? 'The routine'} has ${questions.length} questions`}
        </h3>
        <span className="text-[11.5px] text-mute">answered together</span>
      </div>

      <ol className="m-0 mt-3 flex list-none flex-col gap-4 p-0">
        {questions.map((question, i) => {
          const draft = drafts[question.id];
          return (
            <li key={question.id} className="flex flex-col gap-2">
              <div className="text-[13.5px] leading-snug text-t1">
                <span className="mr-1.5 font-mono text-[11.5px] text-mute">{i + 1}.</span>
                <span className="font-[620]">{question.question}</span>
              </div>
              {question.context ? (
                <p className="m-0 text-[12.5px] leading-relaxed whitespace-pre-line text-soft">{question.context}</p>
              ) : null}

              {question.kind === 'text' ? (
                <Textarea
                  rows={2}
                  value={draft?.text ?? ''}
                  onChange={(event) => set(question.id, { text: event.target.value })}
                  placeholder="Your answer"
                  aria-label={question.question}
                />
              ) : (
                <div role="radiogroup" aria-label={question.question} className="flex flex-col gap-1.5">
                  {[...question.options, ...(question.allow_other ? [OTHER] : [])].map((option) => {
                    const chosen = draft?.choice === option;
                    return (
                      <button
                        key={option}
                        type="button"
                        role="radio"
                        aria-checked={chosen}
                        onClick={() => set(question.id, { choice: option })}
                        className={cn(
                          'rg-focus flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 text-left text-[13px] leading-snug',
                          chosen
                            ? 'border-accent bg-accent/10 text-hi'
                            : 'border-line bg-panel text-body hover:border-btnbd hover:text-t1',
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'mt-[3px] grid size-3.5 shrink-0 place-items-center rounded-full border',
                            chosen ? 'border-accent' : 'border-btnbd',
                          )}
                        >
                          {chosen ? <span className="size-1.5 rounded-full bg-accent" /> : null}
                        </span>
                        {option === OTHER ? <span className="text-soft">Other…</span> : option}
                      </button>
                    );
                  })}
                  {draft?.choice === OTHER ? (
                    <input
                      autoFocus
                      value={draft.text}
                      onChange={(event) => set(question.id, { text: event.target.value })}
                      placeholder="Write your own answer"
                      aria-label={`Other answer to: ${question.question}`}
                      className={inputClass}
                    />
                  ) : null}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <div className="mt-4 flex items-center gap-3">
        <Button tone="primary" disabled={sending || remaining > 0} onClick={() => void send()}>
          {sending ? 'Sending…' : questions.length === 1 ? 'Send answer' : 'Send answers'}
        </Button>
        <span className="text-[12px] text-mute">
          {remaining > 0
            ? `${remaining} still to answer`
            : task.pr_url
              ? 'Posted to the pull request as one comment'
              : 'Delivered to the pull request once it opens'}
        </span>
      </div>
    </section>
  );
}
